import React, { useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import BannerMenu from '@/components/EditorBuilder/BannerMenu';
import { mainContentStyles } from '@/components/EditorBuilder/styles';
import { Image } from '@/components/Image';
import AddCoverDialog from '@/modules/corporate/components/AddCoverDialog';
import { CreateMediaInput, MediaKindEnum } from '@/types/schema';

import LessonTitle from '../LessonTitle';
import RepositioningBanner from '../RepositionBanner';
import useBanner from './hooks/useBanner';
import { bannerOverlayStyles, containerStyles, imageStyles, titleStyles } from './styles';
import { BannerProps } from './types';

const BannerWithTitle: React.FC<BannerProps> = (props) => {
  const {
    showCoverDialog,
    toggleCoverDialog,
    canRemoveBanner = true,
    titleProps,
    cover,
    onCoverSave,
    className,
  } = props;

  const [isRepositioning, { toggle: toggleRepositioning }] = useToggle(false);
  const [yOffsetPercent, setYOffsetPercent] = useState(cover.yOffsetPercent);
  const [naturalImageHeight, setNaturalImageHeight] = useState<number>(0);

  const { onMenuAction, onSavePosition, onCancel, onSubmitCover } = useBanner(
    toggleCoverDialog,
    onCoverSave,
    toggleRepositioning,
    yOffsetPercent,
    setYOffsetPercent,
    cover,
  );

  return (
    <>
      {cover.src ? (
        <Box
          className={cn(
            'group relative mb-16 w-full flex-shrink-0 overflow-hidden',
            containerStyles,
            className,
          )}>
          {isRepositioning ? (
            <RepositioningBanner
              imageSrc={cover.src}
              naturalImageHeight={naturalImageHeight}
              setYOffsetPercent={setYOffsetPercent}
              yOffsetPercent={yOffsetPercent}
              onCancel={onCancel}
              onSavePosition={onSavePosition}
            />
          ) : (
            <>
              <BannerMenu
                canRemoveBanner={canRemoveBanner}
                data-id="banner-action-menu"
                onAction={onMenuAction}
              />
              <Image
                alt="lesson banner"
                className="h-full w-full"
                classes={{ image: imageStyles(yOffsetPercent) }}
                data-id="banner-image"
                layout="fill"
                objectFit="cover"
                src={cover.src}
                onLoad={(e) => setNaturalImageHeight(e.currentTarget.naturalHeight)}
              />
              {titleProps ? (
                <>
                  <Box className={cn('absolute top-0 h-full w-full', bannerOverlayStyles)} />
                  <Stack className="w-full" justifyContent="center">
                    <LessonTitle
                      className={cn('absolute bottom-80', mainContentStyles, titleStyles)}
                      color="text-text-white"
                      {...titleProps}
                    />
                  </Stack>
                </>
              ) : null}
            </>
          )}
        </Box>
      ) : null}
      {showCoverDialog ? (
        <AddCoverDialog
          cover={{ ...cover, kind: MediaKindEnum.Image } as CreateMediaInput}
          onClose={toggleCoverDialog}
          onSubmit={onSubmitCover}
        />
      ) : null}
    </>
  );
};

export default BannerWithTitle;
